import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Lock, Trash2, X, BarChart3 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { SpaceSection } from "@/components/SpaceSection";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TEMPLATES = [
  { label: "Aprobar / Rechazar", title: "¿Aprobamos la propuesta?", type: "yesno", options: [] },
  { label: "Confirmar asistencia", title: "¿Vas a asistir?", type: "yesno", options: [] },
  { label: "Elegir fecha", title: "¿Qué día te viene mejor?", type: "multiple", options: ["Viernes", "Sábado", "Domingo"] },
  { label: "Elegir opción", title: "¿Qué opción prefieres?", type: "single", options: ["Opción A", "Opción B"] },
];

const newOption = (text = "") => ({ id: crypto.randomUUID(), text });

const CreatePollDialog = ({ workspaceId, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [type, setType] = useState("single");
  const [options, setOptions] = useState([newOption(), newOption()]);
  const [anonymous, setAnonymous] = useState(false);
  const [closesAt, setClosesAt] = useState("");
  const [saving, setSaving] = useState(false);

  const applyTemplate = (t) => {
    setTitle(t.title);
    setType(t.type);
    setOptions(t.options.length ? t.options.map((text) => newOption(text)) : [newOption(), newOption()]);
  };

  const create = async () => {
    if (!title.trim()) return toast.error("El título es obligatorio");
    setSaving(true);
    try {
      await api.post(`/workspaces/${workspaceId}/polls`, {
        title, type, options: type === "yesno" ? [] : options.map((o) => o.text),
        anonymous, closes_at: closesAt ? new Date(closesAt).toISOString() : null,
      });
      qc.invalidateQueries({ queryKey: ["polls", workspaceId] });
      toast.success("Encuesta creada");
      onOpenChange(false);
      setTitle(""); setOptions([newOption(), newOption()]); setType("single"); setAnonymous(false); setClosesAt("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="create-poll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Nueva encuesta</DialogTitle>
          <DialogDescription>Decisiones rápidas sin conversaciones interminables.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            {TEMPLATES.map((t) => (
              <button key={t.label} onClick={() => applyTemplate(t)} data-testid={`poll-template-${t.label}`}
                className="text-xs border rounded-full px-3 py-1 hover:bg-secondary transition-colors">
                {t.label}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <Label>Pregunta</Label>
            <Input data-testid="poll-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="¿Qué cenamos el sábado?" maxLength={200} />
          </div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            <div className="flex gap-1.5">
              {[["single", "Opción única"], ["multiple", "Múltiple"], ["yesno", "Sí / No"]].map(([v, l]) => (
                <button key={v} onClick={() => setType(v)} data-testid={`poll-type-${v}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${type === v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {type !== "yesno" && (
            <div className="space-y-2">
              <Label>Opciones</Label>
              {options.map((o, i) => (
                <div key={o.id} className="flex gap-2">
                  <Input data-testid={`poll-option-input-${i}`} value={o.text}
                    onChange={(e) => setOptions(options.map((x) => (x.id === o.id ? { ...x, text: e.target.value } : x)))}
                    placeholder={`Opción ${i + 1}`} maxLength={100} className="h-9" />
                  {options.length > 2 && (
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0"
                      onClick={() => setOptions(options.filter((x) => x.id !== o.id))}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {options.length < 20 && (
                <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="poll-add-option-btn"
                  onClick={() => setOptions([...options, newOption()])}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Añadir opción
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <Label>Votación anónima</Label>
              <p className="text-xs text-muted-foreground">Nadie verá quién votó cada opción</p>
            </div>
            <Switch data-testid="poll-anonymous-switch" checked={anonymous} onCheckedChange={setAnonymous} />
          </div>
          <div className="space-y-2">
            <Label>Cierre automático (opcional)</Label>
            <Input type="datetime-local" data-testid="poll-closes-input" value={closesAt}
              onChange={(e) => setClosesAt(e.target.value)} className="h-9" />
          </div>
          <Button className="w-full" data-testid="poll-create-submit-btn" onClick={create} disabled={saving}>
            {saving ? "Creando..." : "Crear encuesta"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PollCard = ({ poll, workspaceId, isAdmin, myId }) => {
  const qc = useQueryClient();
  const [selection, setSelection] = useState(poll.my_vote || []);
  const isActive = poll.status === "active";
  const hasVoted = (poll.my_vote || []).length > 0;
  const canManage = poll.created_by === myId || isAdmin;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["polls", workspaceId] });

  const toggleOption = (oid) => {
    if (poll.type === "multiple") {
      setSelection((s) => (s.includes(oid) ? s.filter((x) => x !== oid) : [...s, oid]));
    } else {
      setSelection([oid]);
    }
  };

  const vote = async () => {
    if (!selection.length) return toast.error("Selecciona una opción");
    try {
      await api.post(`/polls/${poll.id}/vote`, { option_ids: selection });
      invalidate();
      toast.success("Voto registrado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const close = async () => {
    try {
      await api.post(`/polls/${poll.id}/close`);
      invalidate();
      toast.success("Encuesta cerrada");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/polls/${poll.id}`);
      invalidate();
      toast.success("Encuesta eliminada");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Card className="p-5 space-y-4" data-testid={`poll-card-${poll.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold" data-testid={`poll-title-${poll.id}`}>{poll.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {poll.created_by_name} · {timeAgo(poll.created_at)} · {poll.total_voters} {poll.total_voters === 1 ? "voto" : "votos"}
            {poll.anonymous && <span className="inline-flex items-center gap-0.5 ml-1"><Lock className="h-3 w-3" /> anónima</span>}
          </p>
        </div>
        <Badge variant={isActive ? "default" : "secondary"} data-testid={`poll-status-${poll.id}`}>
          {isActive ? "Activa" : "Cerrada"}
        </Badge>
        {canManage && (
          <div className="flex gap-1">
            {isActive && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`poll-close-btn-${poll.id}`} onClick={close}>
                Cerrar
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" data-testid={`poll-delete-btn-${poll.id}`} onClick={remove}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {poll.results.map((opt) => {
          const isSelected = selection.includes(opt.id);
          const isMyVote = (poll.my_vote || []).includes(opt.id);
          return (
            <div key={opt.id}>
              <button
                disabled={!isActive}
                onClick={() => toggleOption(opt.id)}
                data-testid={`poll-option-${poll.id}-${opt.id}`}
                className={`w-full text-left relative overflow-hidden rounded-lg border px-3 py-2 transition-colors ${
                  isSelected && isActive ? "border-primary" : ""
                } ${!isActive ? "cursor-default" : "hover:border-primary/50"}`}>
                <div className="absolute inset-y-0 left-0 bg-primary/10 transition-all" style={{ width: `${opt.pct}%` }} />
                <div className="relative flex items-center gap-2">
                  {isActive && (poll.type === "multiple"
                    ? <Checkbox checked={isSelected} className="pointer-events-none" />
                    : <span className={`h-3.5 w-3.5 rounded-full border-2 ${isSelected ? "border-primary bg-primary" : "border-muted-foreground/40"}`} />)}
                  <span className="text-sm flex-1">{opt.text} {isMyVote && <span className="text-primary">✓</span>}</span>
                  <span className="text-xs font-semibold">{opt.pct}%</span>
                  <span className="text-xs text-muted-foreground">({opt.count})</span>
                </div>
                {opt.voters && opt.voters.length > 0 && (
                  <p className="relative text-[10px] text-muted-foreground mt-0.5 truncate">{opt.voters.join(", ")}</p>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {isActive && (
        <Button size="sm" data-testid={`poll-vote-btn-${poll.id}`} onClick={vote}>
          {hasVoted ? "Cambiar voto" : "Votar"}
        </Button>
      )}
    </Card>
  );
};

export default function PollsPage() {
  const { id: workspaceId } = useParams();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: polls = [] } = useQuery({
    queryKey: ["polls", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/polls`)).data,
    refetchInterval: 8000,
  });
  const { data: ws } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}`)).data,
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data });

  const isAdmin = ["owner", "admin"].includes(ws?.my_role);
  const active = polls.filter((p) => p.status === "active");
  const closed = polls.filter((p) => p.status !== "active");

  return (
    <SpaceSection title="Encuestas" actions={
      <Button size="sm" data-testid="polls-create-btn" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" /> Nueva encuesta
      </Button>
    }>
      {polls.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <BarChart3 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Crea la primera encuesta y decide en segundos, no en cien mensajes.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {active.map((p) => <PollCard key={p.id} poll={p} workspaceId={workspaceId} isAdmin={isAdmin} myId={me?.id} />)}
            </div>
          )}
          {closed.length > 0 && (
            <>
              <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Cerradas</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {closed.map((p) => <PollCard key={p.id} poll={p} workspaceId={workspaceId} isAdmin={isAdmin} myId={me?.id} />)}
              </div>
            </>
          )}
        </div>
      )}
      <CreatePollDialog workspaceId={workspaceId} open={createOpen} onOpenChange={setCreateOpen} />
    </SpaceSection>
  );
}
